/**
 * California statewide electricity (kWh) and natural gas (therms) from
 * ghg_inventory_precalculated State rows — same basis as upsert_california_state_ghg_from_counties
 * (sums of CA counties). Use for Energy charts when geography is California State (geofips 6).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { CALIFORNIA_STATE_GEOFIPS } from './californiaGhgInventoryVmtService';

export interface CaliforniaStateEnergyActivityRow {
  year: number;
  elec_residential_kwh: number;
  elec_commercial_kwh: number;
  /** MT CO₂e from precalc when present (preferred for GHG charts vs recomputing from kWh). */
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  ng_residential_therms: number;
  ng_commercial_therms: number;
}

function numNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Paginated fetch of State geography rows for California (geofips 6).
 */
export async function fetchCaliforniaStateEnergyActivityFromGhgInventory(
  client: SupabaseClient
): Promise<CaliforniaStateEnergyActivityRow[]> {
  const rows: CaliforniaStateEnergyActivityRow[] = [];
  const pageSize = 1000;
  let from = 0;

  for (;;) {
    const { data, error } = await client
      .from('ghg_inventory_precalculated')
      .select(
        'year, elec_residential_kwh, elec_commercial_kwh, elec_residential_emissions, elec_commercial_emissions, ng_residential_therms, ng_commercial_therms'
      )
      .eq('geofips', CALIFORNIA_STATE_GEOFIPS)
      .eq('geography_type', 'State')
      .order('year', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) {
      return [];
    }

    const batch = data ?? [];
    if (batch.length === 0) break;

    for (const r of batch) {
      const y = Number(r.year);
      if (!Number.isFinite(y)) continue;
      rows.push({
        year: y,
        elec_residential_kwh: num(r.elec_residential_kwh),
        elec_commercial_kwh: num(r.elec_commercial_kwh),
        elec_residential_emissions: numNull(r.elec_residential_emissions),
        elec_commercial_emissions: numNull(r.elec_commercial_emissions),
        ng_residential_therms: num(r.ng_residential_therms),
        ng_commercial_therms: num(r.ng_commercial_therms)
      });
    }

    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function isCaliforniaStateFromPopulationRow(row: {
  geofips?: string | number | null;
  geography_type?: string | null;
}): boolean {
  const g = String(row.geofips ?? '').replace(/^0+/, '') || '0';
  const gt = String(row.geography_type || '').toLowerCase();
  return g === String(CALIFORNIA_STATE_GEOFIPS) && gt === 'state';
}
